import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import {
  activityTypes as activityTypesApi,
  listAttendants,
  pipelinesApi,
} from '../../lib/resources';
import { DUE_UNITS, FLAG_LABELS, describeDue } from '../../lib/stage-rule-fields';
import type {
  PipelineComplete,
  PipelineMemberRow,
  Stage,
  StageAutoActivity,
} from '../../types';
import {
  Button,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Tabs,
  Toggle,
  compactInputClass,
  inputClass,
} from '../ui';
import { StageRulesEditor } from './StageRulesEditor';

export type EditPipelineTab = 'config' | 'atendentes' | 'atividades' | 'condicionais';

const TABS: Array<[EditPipelineTab, string]> = [
  ['config', 'Configurações'],
  ['atendentes', 'Atendentes'],
  ['atividades', 'Atividades por etapa'],
  ['condicionais', 'Condicionais'],
];

/** "Editar pipeline": as 4 abas de configuração. Cada aba salva sozinha. */
export function EditPipelineDialog({
  pipeline,
  stages,
  initialTab = 'config',
  onClose,
  onChanged,
}: {
  pipeline: PipelineComplete;
  stages: Stage[];
  initialTab?: EditPipelineTab;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<EditPipelineTab>(initialTab);
  return (
    <Modal title={`Editar pipeline — ${pipeline.name}`} onClose={onClose} wide>
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      <div className="mt-4 max-h-[65vh] overflow-y-auto pr-1">
        {tab === 'config' && <ConfigTab pipeline={pipeline} onChanged={onChanged} />}
        {tab === 'atendentes' && <MembersTab pipelineId={pipeline.id} />}
        {tab === 'atividades' && (
          <AutoActivitiesTab pipelineId={pipeline.id} stages={stages} onChanged={onChanged} />
        )}
        {tab === 'condicionais' && (
          <StageRulesEditor pipelineId={pipeline.id} stages={stages} />
        )}
      </div>
    </Modal>
  );
}

function ConfigTab({
  pipeline,
  onChanged,
}: {
  pipeline: PipelineComplete;
  onChanged: () => void;
}) {
  const [name, setName] = useState(pipeline.name);
  const [group, setGroup] = useState(pipeline.group ?? '');
  const [description, setDescription] = useState(pipeline.description ?? '');
  const groups = useQuery({ queryKey: ['pipelines', 'groups'], queryFn: pipelinesApi.groups });
  const save = useMutation({
    mutationFn: () =>
      pipelinesApi.update(pipeline.id, {
        name: name.trim(),
        group: group.trim() || null,
        description: description.trim() || null,
      }),
    onSuccess: onChanged,
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <Field label="Nome *">
        <input
          className={inputClass}
          value={name}
          maxLength={160}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Grupo">
        <input
          className={inputClass}
          list="edit-pipeline-groups"
          value={group}
          maxLength={120}
          onChange={(event) => setGroup(event.target.value)}
        />
        <datalist id="edit-pipeline-groups">
          {(groups.data ?? []).map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
      </Field>
      <Field label="Descrição">
        <textarea
          rows={3}
          className={inputClass}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      {save.error && <ErrorNote>{errorMessage(save.error, 'Falha ao salvar')}</ErrorNote>}
      {save.isSuccess && <p className="text-xs text-emerald-600">Salvo.</p>}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
          Salvar
        </Button>
      </div>
    </form>
  );
}

function MembersTab({ pipelineId }: { pipelineId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['pipeline-members', pipelineId],
    queryFn: () => pipelinesApi.members(pipelineId),
  });
  const [draft, setDraft] = useState<PipelineMemberRow[] | null>(null);
  const rows = draft ?? query.data ?? [];

  const save = useMutation({
    mutationFn: () =>
      pipelinesApi.setMembers(
        pipelineId,
        rows
          .filter((row) => row.isMember)
          .map((row) => ({
            membershipId: row.membershipId,
            showOnlyMine: row.showOnlyMine,
            disableCreate: row.disableCreate,
            disableDelete: row.disableDelete,
            disableAttendantChange: row.disableAttendantChange,
          })),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(['pipeline-members', pipelineId], result);
      setDraft(null);
    },
  });

  const update = (membershipId: string, patch: Partial<PipelineMemberRow>) =>
    setDraft(
      rows.map((row) => (row.membershipId === membershipId ? { ...row, ...patch } : row)),
    );

  if (query.isLoading) return <Loading />;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Só quem está marcado vê esta pipeline. Administradores e quem tem o papel "Administrador
        de pipelines" veem todas, sem travas.
      </p>
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
        {rows.map((row) => (
          <div key={row.membershipId} className="space-y-2 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={row.fullAccess || row.isMember}
                  disabled={row.fullAccess}
                  onChange={(event) =>
                    update(row.membershipId, { isMember: event.target.checked })
                  }
                />
                <span className="font-medium text-slate-800">{row.name || row.email}</span>
                <span className="text-xs text-slate-400">{row.email}</span>
              </label>
              {row.fullAccess && (
                <span className="text-xs text-slate-400">vê todas as pipelines</span>
              )}
            </div>
            {row.isMember && !row.fullAccess && (
              <div className="grid gap-2 pl-6 sm:grid-cols-2">
                {FLAG_LABELS.map(([flag, label]) => (
                  <Toggle
                    key={flag}
                    checked={row[flag]}
                    onChange={(checked) => update(row.membershipId, { [flag]: checked })}
                    label={label}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {save.error && <ErrorNote>{errorMessage(save.error, 'Falha ao salvar')}</ErrorNote>}
      <div className="flex items-center justify-end gap-3">
        {draft && (
          <span className="text-xs font-medium text-amber-600">Alterações não salvas</span>
        )}
        <Button
          variant="primary"
          disabled={!draft || save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar atendentes
        </Button>
      </div>
    </div>
  );
}

function AutoActivitiesTab({
  pipelineId,
  stages,
  onChanged,
}: {
  pipelineId: string;
  stages: Stage[];
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['pipeline-auto-activities', pipelineId],
    queryFn: () => pipelinesApi.autoActivities(pipelineId),
  });
  const types = useQuery({
    queryKey: ['activity-types'],
    queryFn: () => activityTypesApi.list(),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['pipeline-auto-activities', pipelineId] });
    onChanged();
  };

  const toggleRequired = useMutation({
    mutationFn: (item: StageAutoActivity) =>
      pipelinesApi.updateAutoActivity(item.stageId, item.id, { required: !item.required }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (item: StageAutoActivity) =>
      pipelinesApi.removeAutoActivity(item.stageId, item.id),
    onSuccess: refresh,
  });

  if (query.isLoading) return <Loading />;
  const items = query.data ?? [];
  const assigneeName = (assignee: string) =>
    assignee === 'BUSINESS_ATTENDANT'
      ? 'Atendente do negócio'
      : (attendants.data?.data.find((item) => item.id === assignee)?.name ?? 'Membro');

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Quando um negócio entra na etapa, cada atividade abaixo é criada para ele. Atividade
        obrigatória aberta segura o negócio na etapa até ser concluída.
      </p>
      {stages.map((stage) => (
        <section key={stage.id} className="rounded-xl border border-slate-200">
          <header className="border-b border-slate-200 px-4 py-2 text-sm font-semibold text-slate-800">
            {stage.name}
          </header>
          <ul className="divide-y divide-slate-100">
            {items
              .filter((item) => item.stageId === stage.id)
              .map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-slate-800">
                      {item.title}
                      {item.required && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                          obrigatória
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-400">
                      {types.data?.data.find((type) => type.id === item.activityTypeId)?.name ??
                        'Sem tipo'}{' '}
                      · vence {describeDue(item.dueInMinutes)} · {assigneeName(item.assignee)}
                    </p>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => toggleRequired.mutate(item)}
                      className="text-slate-600 hover:underline"
                    >
                      {item.required ? 'Tornar opcional' : 'Tornar obrigatória'}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove.mutate(item)}
                      className="text-red-600 hover:underline"
                    >
                      Remover
                    </button>
                  </div>
                </li>
              ))}
          </ul>
          <AutoActivityForm
            stageId={stage.id}
            types={types.data?.data ?? []}
            attendants={attendants.data?.data ?? []}
            onCreated={refresh}
          />
        </section>
      ))}
    </div>
  );
}

function AutoActivityForm({
  stageId,
  types,
  attendants,
  onCreated,
}: {
  stageId: string;
  types: Array<{ id: string; name: string }>;
  attendants: Array<{ id: string; name: string }>;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [typeId, setTypeId] = useState('');
  const [amount, setAmount] = useState('1');
  const [unit, setUnit] = useState<(typeof DUE_UNITS)[number][0]>('days');
  const [assignee, setAssignee] = useState('BUSINESS_ATTENDANT');
  const [required, setRequired] = useState(false);

  const create = useMutation({
    mutationFn: () => {
      const size = DUE_UNITS.find(([key]) => key === unit)?.[2] ?? 1;
      return pipelinesApi.createAutoActivity(stageId, {
        title: title.trim(),
        activityTypeId: typeId || undefined,
        dueInMinutes: Math.max(0, Math.round(Number(amount.replace(',', '.')) || 0) * size),
        assignee,
        required,
      });
    },
    onSuccess: () => {
      setTitle('');
      setRequired(false);
      onCreated();
    },
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <input
        className={`${compactInputClass} w-52`}
        placeholder="Nova atividade…"
        value={title}
        maxLength={200}
        onChange={(event) => setTitle(event.target.value)}
      />
      <select
        className={`${compactInputClass} w-36`}
        value={typeId}
        onChange={(event) => setTypeId(event.target.value)}
      >
        <option value="">Sem tipo</option>
        {types.map((type) => (
          <option key={type.id} value={type.id}>
            {type.name}
          </option>
        ))}
      </select>
      <span className="pb-2 text-xs text-slate-500">vence em</span>
      <input
        className={`${compactInputClass} w-16`}
        inputMode="numeric"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
      />
      <select
        className={`${compactInputClass} w-28`}
        value={unit}
        onChange={(event) => setUnit(event.target.value as typeof unit)}
      >
        {DUE_UNITS.map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
      <select
        className={`${compactInputClass} w-44`}
        value={assignee}
        onChange={(event) => setAssignee(event.target.value)}
      >
        <option value="BUSINESS_ATTENDANT">Atendente do negócio</option>
        {attendants.map((attendant) => (
          <option key={attendant.id} value={attendant.id}>
            {attendant.name}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={required}
          onChange={(event) => setRequired(event.target.checked)}
        />
        obrigatória
      </label>
      <Button type="submit" disabled={!title.trim() || create.isPending}>
        Adicionar
      </Button>
      {create.error && (
        <p className="w-full text-xs text-red-600">
          {errorMessage(create.error, 'Falha ao adicionar')}
        </p>
      )}
    </form>
  );
}
